import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask } from 'pdfjs-dist';

/**
 * Opening an attachment.
 *
 * From where somebody is sitting there is one action — "let me see that file" — and the
 * answer should not depend on what the file turned out to be. So there is one shell: the
 * same dialog, the same title, the same download button in the same corner, whether what
 * arrives is a scanned inspection report or a photograph of a rusted damper. Only the middle
 * changes, and it changes on the content type, never through pdf.js.
 *
 * The previous behaviour was `target="_blank"` on every attachment, which hands the file to
 * the browser and loses the context it was opened from. With 8 024 PDFs and fifty thousand
 * photographs in the imported history, that is a tab per file and no way back.
 *
 * Three rules the whole thing is built around:
 *
 *   - **Never a blank frame and never an endless spinner.** Every pane has a failure state
 *     that says what happened and still offers the file. A `.heic` from an iPhone, a PDF
 *     that was truncated on its way into the previous system, a file the export recorded but
 *     never copied — all of them are real here, and all of them must read as an explanation
 *     rather than as the application being broken.
 *   - **One page at a time.** Some of these are forty-megabyte scans. Rendering forty pages
 *     to reach page one is how a viewer earns a reputation for being slow.
 *   - **The keyboard drives it.** Escape closes, the arrows page, and focus goes back to the
 *     thumbnail that was clicked — which the native `<dialog>` does for us, and is the main
 *     reason this is a `<dialog>` and not a `<div>` with a scrim.
 */

export type ViewerFile = {
  id: string;
  fileName: string;
  /** As resolved by the API, which corrects the previous system's `application/octet-stream`. */
  contentType: string | null;
  /**
   * Complete, and carrying the business slug. Built by the API and used verbatim: the slug
   * is a tenancy boundary, and a URL assembled in the browser is a URL that can be assembled
   * for the wrong business.
   */
  url: string;
  /** The same bytes with `Content-Disposition: attachment`. Falls back to `url`. */
  downloadUrl?: string | null;
  caption?: string | null;
  byteSize?: number | null;
};

export type FileKind = 'pdf' | 'image' | 'video' | 'audio' | 'other';

/**
 * Extensions worth trusting over a missing type.
 *
 * The API resolves the content type before it answers, so this is the second line rather
 * than the first — it catches a row that reached the client with nothing usable at all.
 */
const EXTENSION_KIND: Record<string, FileKind> = {
  pdf: 'pdf',
  jpg: 'image', jpeg: 'image', png: 'image', gif: 'image', webp: 'image',
  bmp: 'image', tif: 'image', tiff: 'image', heic: 'image', heif: 'image', avif: 'image',
  mp4: 'video', mov: 'video', m4v: 'video', webm: 'video', '3gp': 'video',
  mp3: 'audio', m4a: 'audio', ogg: 'audio', wav: 'audio', aac: 'audio',
};

/** What to show this file with. */
export function fileKind(file: { contentType?: string | null; fileName?: string | null }): FileKind {
  const type = (file.contentType ?? '').split(';')[0]!.trim().toLowerCase();
  if (type === 'application/pdf') return 'pdf';
  /*
   * SVG is deliberately not an image here. The API refuses to serve it inline — it would run
   * as a document in the portal's own origin — so rendering it in an `<img>` would show a
   * broken picture instead of the honest "download this one" the shell already has.
   */
  if (type === 'image/svg+xml') return 'other';
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';

  const extension = (file.fileName ?? '').split('.').pop()?.toLowerCase() ?? '';
  return EXTENSION_KIND[extension] ?? 'other';
}

/**
 * A file size somebody can read.
 *
 * Kilo- and mega- rather than kibi-: a coordinator comparing this to what their file manager
 * says needs the same number, not the technically correct one.
 */
export function formatBytes(bytes: number | null | undefined, language: string): string {
  if (!bytes || bytes < 0) return '';
  const french = !language.startsWith('en');
  if (bytes < 1000) return french ? `${bytes} o` : `${bytes} B`;
  if (bytes < 1_000_000) return `${Math.round(bytes / 1000)} ${french ? 'ko' : 'KB'}`;
  const megabytes = bytes / 1_000_000;
  return `${megabytes.toFixed(megabytes < 10 ? 1 : 0)} ${french ? 'Mo' : 'MB'}`;
}

/**
 * The viewer, wired to a screen.
 *
 * The dialog is rendered by the screen rather than teleported to the document body, and it
 * is mounted whether or not anything is open. Both of those are on purpose: a `<dialog>`
 * driven by `showModal()` already escapes its container into the browser's top layer, so a
 * portal buys nothing, and mounting it once means opening a file is a state change rather
 * than a mount — which is what keeps the browser's own focus bookkeeping intact.
 */
export function useDocumentViewer(): {
  open: (file: ViewerFile) => void;
  close: () => void;
  element: ReactNode;
} {
  const [file, setFile] = useState<ViewerFile | null>(null);
  const open = useCallback((next: ViewerFile) => setFile(next), []);
  const close = useCallback(() => setFile(null), []);

  return {
    open,
    close,
    element: <DocumentViewer file={file} onClose={close} />,
  };
}

function DocumentViewer({ file, onClose }: { file: ViewerFile | null; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (file && !dialog.open) dialog.showModal();
    if (!file && dialog.open) dialog.close();
  }, [file]);

  const kind = file ? fileKind(file) : 'other';
  const downloadUrl = file ? file.downloadUrl ?? file.url : '';

  return (
    <dialog
      ref={dialogRef}
      className="viewer"
      aria-label={file?.fileName ?? ''}
      // The `close` event fires however the dialog was dismissed — Escape, the button, the
      // backdrop — so there is exactly one path back to the screen's state.
      onClose={onClose}
      // Clicking the backdrop lands on the dialog element itself; a click on anything inside
      // has that child as its target.
      onClick={(event) => {
        if (event.target === dialogRef.current) dialogRef.current?.close();
      }}
    >
      {file && (
        <>
          <header className="viewer__bar">
            <div className="viewer__title">
              <span className="viewer__name" title={file.fileName}>
                {file.fileName}
              </span>
              <span className="viewer__meta">
                {file.caption && <span className="viewer__caption">{file.caption}</span>}
                {file.byteSize ? <span className="tnum">{formatBytes(file.byteSize, i18n.language)}</span> : null}
              </span>
            </div>

            <div className="viewer__actions">
              <a className="btn btn--ghost" href={file.url} target="_blank" rel="noreferrer">
                {t('viewer.openTab')}
              </a>
              {/* The server also marks this response as an attachment, which is what makes
                  the save happen; the attribute only supplies the name. */}
              <a className="btn btn--primary" href={downloadUrl} download={file.fileName}>
                {t('viewer.download')}
              </a>
              <button
                type="button"
                className="viewer__close"
                onClick={() => dialogRef.current?.close()}
                aria-label={t('common.close')}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
          </header>

          <div className="viewer__stage">
            {kind === 'pdf' && <PdfPane key={file.id} url={file.url} downloadUrl={downloadUrl} fileName={file.fileName} />}
            {kind === 'image' && <ImagePane key={file.id} file={file} downloadUrl={downloadUrl} />}
            {(kind === 'video' || kind === 'audio') && (
              <MediaPane key={file.id} file={file} kind={kind} downloadUrl={downloadUrl} />
            )}
            {kind === 'other' && <NoPreview downloadUrl={downloadUrl} fileName={file.fileName} />}
          </div>
        </>
      )}
    </dialog>
  );
}

// ------------------------------------------------------------------------- PDF ----

const MIN_SCALE = 0.25;
const MAX_SCALE = 5;
/**
 * A hair off the fitted width.
 *
 * The stage's own padding is already excluded — a `ResizeObserver` reports the content box,
 * and a scroll container's scrollbar comes out of that box too — so this is only insurance
 * against a page that rounds up by a pixel and provokes a horizontal scrollbar, which then
 * shortens the stage and starts the measurement oscillating.
 */
const FIT_MARGIN = 4;

function PdfPane({ url, downloadUrl, fileName }: { url: string; downloadUrl: string; fileName: string }) {
  const { t } = useTranslation();
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const documentRef = useRef<PDFDocumentProxy | null>(null);
  const taskRef = useRef<RenderTask | null>(null);

  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [pageCount, setPageCount] = useState(0);
  const [page, setPage] = useState(1);
  /** `null` is fit-to-width, which is what a document opens at. */
  const [zoom, setZoom] = useState<number | null>(null);
  const [stageWidth, setStageWidth] = useState(0);
  /** The current page's width in CSS pixels at scale 1, so the buttons can reason about fit. */
  const [pageWidth, setPageWidth] = useState(0);

  // ---- the document -------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    let loading: PDFDocumentLoadingTask | null = null;

    void (async () => {
      try {
        // pdf.js arrives here and nowhere else, so a session that never opens a PDF never
        // downloads it.
        const { openDocument } = await import('../lib/pdfjs.js');
        if (cancelled) return;

        loading = openDocument(url);
        const pdf = await loading.promise;
        if (cancelled) {
          void pdf.destroy();
          return;
        }

        documentRef.current = pdf;
        setPageCount(pdf.numPages);
        setStatus('ready');
      } catch {
        /*
         * Deliberately one state for every reason. A password-protected file, a truncated
         * one and a `.pdf` that is really a Word document all mean the same thing to the
         * person looking at it — this will not open, here is the file — and inventing three
         * messages would only make two of them wrong.
         */
        if (!cancelled) setStatus('failed');
      }
    })();

    return () => {
      cancelled = true;
      taskRef.current?.cancel();
      taskRef.current = null;
      documentRef.current = null;
      // Tears down the worker with it, so closing the dialog releases the memory a large
      // scan was holding rather than keeping it for a document nobody is looking at.
      void loading?.destroy();
    };
  }, [url]);

  // ---- how wide the page may be --------------------------------------------------
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setStageWidth(entry.contentRect.width);
    });
    observer.observe(stage);
    setStageWidth(stage.clientWidth);
    return () => observer.disconnect();
  }, []);

  // ---- the visible page ----------------------------------------------------------
  useEffect(() => {
    if (status !== 'ready' || stageWidth === 0) return;
    const pdf = documentRef.current;
    const canvas = canvasRef.current;
    if (!pdf || !canvas) return;

    let cancelled = false;

    void (async () => {
      try {
        const pdfPage = await pdf.getPage(page);
        if (cancelled) return;

        const unscaled = pdfPage.getViewport({ scale: 1 });
        setPageWidth(unscaled.width);
        const scale = clamp(zoom ?? (stageWidth - FIT_MARGIN) / unscaled.width, MIN_SCALE, MAX_SCALE);
        const viewport = pdfPage.getViewport({ scale });

        /*
         * Drawn at the device's pixel density and scaled back down with CSS, because a PDF
         * rendered at one device pixel per CSS pixel is visibly soft — and half of what is
         * in here is a scan, where softness reads as a bad scan rather than as a bad viewer.
         * Capped at 2 so a very large page on a 3× display does not allocate a canvas the
         * browser refuses.
         */
        const density = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = Math.ceil(viewport.width * density);
        canvas.height = Math.ceil(viewport.height * density);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;

        /*
         * pdf.js refuses two concurrent renders onto one canvas, and cancelling is not
         * instantaneous — the previous task has to be allowed to reject before the next one
         * may start. Holding pages down with the arrow keys is exactly how that race is hit.
         */
        const previous = taskRef.current;
        if (previous) {
          previous.cancel();
          await previous.promise.catch(() => undefined);
        }
        if (cancelled) return;

        const task = pdfPage.render({
          canvas,
          viewport,
          transform: density === 1 ? undefined : [density, 0, 0, density, 0, 0],
        });
        taskRef.current = task;
        await task.promise;
        // Releases the operator list for a page that is no longer on screen. Without it,
        // paging through a hundred-page scan keeps every page's bitmap alive.
        pdfPage.cleanup();
      } catch (error) {
        // A cancelled render is the normal outcome of paging quickly, not a failure.
        if (!cancelled && !isCancellation(error)) setStatus('failed');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [status, page, zoom, stageWidth]);

  // ---- the keyboard --------------------------------------------------------------
  useEffect(() => {
    if (status !== 'ready') return;
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const step = (delta: number) => {
        event.preventDefault();
        setPage((current) => clamp(current + delta, 1, pageCount));
      };
      switch (event.key) {
        case 'ArrowLeft':
        case 'PageUp':
          return step(-1);
        case 'ArrowRight':
        case 'PageDown':
          return step(1);
        case 'Home':
          event.preventDefault();
          return setPage(1);
        case 'End':
          event.preventDefault();
          return setPage(pageCount);
        default:
          return;
      }
    };
    // On the document rather than the stage: the dialog is modal, so nothing outside it can
    // be focused, and this works wherever inside it the focus happens to be.
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [status, pageCount]);

  const fitScale = pageWidth > 0 ? (stageWidth - FIT_MARGIN) / pageWidth : 1;
  const scale = clamp(zoom ?? fitScale, MIN_SCALE, MAX_SCALE);

  if (status === 'failed') {
    return <Failure downloadUrl={downloadUrl} fileName={fileName} />;
  }

  return (
    <>
      <div className="viewer__scroll" ref={stageRef}>
        {status === 'loading' && (
          <p className="viewer__status" role="status">
            {t('viewer.loading')}
          </p>
        )}
        {/* Kept mounted while loading so the ref exists the moment the document resolves. */}
        <canvas ref={canvasRef} className="viewer__canvas" hidden={status !== 'ready'} />
      </div>

      {status === 'ready' && (
        <footer className="viewer__controls">
          <div className="viewer__pager">
            <button
              type="button"
              className="viewer__step"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page <= 1}
              aria-label={t('viewer.previousPage')}
            >
              <span aria-hidden="true">‹</span>
            </button>
            <span className="viewer__pageLabel tnum" aria-live="polite">
              {t('viewer.page', { page, count: pageCount })}
            </span>
            <button
              type="button"
              className="viewer__step"
              onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
              disabled={page >= pageCount}
              aria-label={t('viewer.nextPage')}
            >
              <span aria-hidden="true">›</span>
            </button>
          </div>

          <div className="viewer__zoom">
            <button
              type="button"
              className="viewer__step"
              onClick={() => setZoom(clamp(scale / 1.25, MIN_SCALE, MAX_SCALE))}
              disabled={scale <= MIN_SCALE + 0.001}
              aria-label={t('viewer.zoomOut')}
            >
              <span aria-hidden="true">−</span>
            </button>
            <span className="viewer__zoomLabel tnum">{Math.round(scale * 100)}%</span>
            <button
              type="button"
              className="viewer__step"
              onClick={() => setZoom(clamp(scale * 1.25, MIN_SCALE, MAX_SCALE))}
              disabled={scale >= MAX_SCALE - 0.001}
              aria-label={t('viewer.zoomIn')}
            >
              <span aria-hidden="true">+</span>
            </button>
            <button
              type="button"
              className={`viewer__fit${zoom === null ? ' viewer__fit--on' : ''}`}
              onClick={() => setZoom(null)}
              aria-pressed={zoom === null}
            >
              {t('viewer.fitWidth')}
            </button>
          </div>
        </footer>
      )}
    </>
  );
}

/** pdf.js signals a cancelled render by name, and it is not an error worth reporting. */
function isCancellation(error: unknown): boolean {
  const name = (error as { name?: string } | null)?.name ?? '';
  return name === 'RenderingCancelledException' || name === 'AbortException';
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

// ----------------------------------------------------------------------- images ----

function ImagePane({ file, downloadUrl }: { file: ViewerFile; downloadUrl: string }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading');
  /** Contained in the stage, or shown at its own size and scrolled. */
  const [actualSize, setActualSize] = useState(false);

  if (status === 'failed') {
    /*
     * Almost always a `.heic` straight off an iPhone, which Safari renders and nothing else
     * does. Saying so is not possible without guessing, so the honest message plus the file
     * is what is offered — the same answer as any other unreadable file.
     */
    return <Failure downloadUrl={downloadUrl} fileName={file.fileName} />;
  }

  return (
    <>
      <div className={`viewer__scroll${actualSize ? ' viewer__scroll--actual' : ''}`}>
        {status === 'loading' && (
          <p className="viewer__status" role="status">
            {t('viewer.loading')}
          </p>
        )}
        <img
          className="viewer__image"
          src={file.url}
          // The technician's own caption is the only honest description available; a file
          // name is not one.
          alt={file.caption ?? file.fileName}
          hidden={status !== 'ready'}
          onLoad={() => setStatus('ready')}
          onError={() => setStatus('failed')}
        />
      </div>

      {status === 'ready' && (
        <footer className="viewer__controls">
          <div className="viewer__zoom">
            <button
              type="button"
              className={`viewer__fit${actualSize ? '' : ' viewer__fit--on'}`}
              onClick={() => setActualSize((current) => !current)}
              aria-pressed={!actualSize}
            >
              {t('viewer.fitWidth')}
            </button>
          </div>
        </footer>
      )}
    </>
  );
}

// ------------------------------------------------------------- video and audio ----

/**
 * Customers send video of a unit making a noise, and technicians record voice notes. The
 * media endpoint answers `Range` requests, which is what lets these seek at all.
 */
function MediaPane({
  file,
  kind,
  downloadUrl,
}: {
  file: ViewerFile;
  kind: 'video' | 'audio';
  downloadUrl: string;
}) {
  const [failed, setFailed] = useState(false);
  if (failed) return <Failure downloadUrl={downloadUrl} fileName={file.fileName} />;

  return (
    <div className="viewer__scroll">
      {kind === 'video' ? (
        <video className="viewer__video" src={file.url} controls preload="metadata" onError={() => setFailed(true)} />
      ) : (
        <audio className="viewer__audio" src={file.url} controls preload="metadata" onError={() => setFailed(true)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- the bad cases ----

function Failure({ downloadUrl, fileName }: { downloadUrl: string; fileName: string }) {
  const { t } = useTranslation();
  return (
    <div className="viewer__fallback" role="alert">
      <p className="viewer__fallbackTitle">{t('viewer.failed')}</p>
      <p className="viewer__fallbackBody">{t('viewer.failedBody')}</p>
      <a className="btn btn--primary" href={downloadUrl} download={fileName}>
        {t('viewer.download')}
      </a>
    </div>
  );
}

function NoPreview({ downloadUrl, fileName }: { downloadUrl: string; fileName: string }) {
  const { t } = useTranslation();
  return (
    <div className="viewer__fallback">
      <p className="viewer__fallbackTitle">{t('viewer.noPreview')}</p>
      <a className="btn btn--primary" href={downloadUrl} download={fileName}>
        {t('viewer.download')}
      </a>
    </div>
  );
}
